import React, { useState, useEffect, useRef } from 'react';

const MessageReactions = ({
  actionId,
  currentPlayerId,
  existingReactions = {},
  onReact,
  theme,
  alignRight = false,
  // 🧹 Nina 08-30: non-comp spaces + the house cap visible chips to the top
  // few on EVERY form factor (resident swarms leave 10+ one-count emojis and
  // the row reads as noise). null = legacy behavior (mobile-only cap of 6).
  chipCap = null,
  // 🏡 The place page's chat (W2d.1, Nina 09-26): all default OFF, so every
  // other mount renders exactly as before. readOnly = the chips only (a guest:
  // no add button, a chip is not a toggle, nothing at all when there are
  // none) · hideAddLabel = no "Add reaction" words beside the 😊 · addClassName
  // = a class on the 😊 (the place shows it on the bubble's hover/focus) ·
  // propUi = the theme shield (data-prop-ui) on the chips, +N and the 😊 ·
  // pickerClassName = a class on the picker (the place re-anchors it).
  readOnly = false,
  hideAddLabel = false,
  addClassName = undefined,
  propUi = false,
  pickerClassName = undefined
}) => {
  const reactions = ['❤️', '😂', '🔥', '😮', '💀', '😈', '👏', '🤔', '👀', '💯', '😭', '⚡', '🫡', '💎', '🗑️', '☠️', '🤯', '😍', '🥶', '👑'];
  const [showPicker, setShowPicker] = useState(false);
  // 📱 Compact mode caps visible chips — bot swarms leave 15+ one-count
  // reactions and the old 40px tiles built a wall (Nina 08-02)
  const [showAllReactions, setShowAllReactions] = useState(false);
  const [userReactions, setUserReactions] = useState(
    existingReactions[currentPlayerId] || []
  );
  const pendingReactionsRef = useRef(new Set());
  const pickerRef = useRef(null);
  const toggleRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close picker when clicking outside
  useEffect(() => {
    if (!showPicker) return;
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target) &&
          toggleRef.current && !toggleRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPicker]);

  // Sync with backend reactions when they change, but don't overwrite pending changes
  useEffect(() => {
    const backendReactions = existingReactions[currentPlayerId] || [];

    // If no pending reactions, just sync from backend
    if (pendingReactionsRef.current.size === 0) {
      setUserReactions(backendReactions);
    } else {
      // Merge backend reactions with pending ones
      const merged = [...new Set([...backendReactions, ...Array.from(pendingReactionsRef.current)])];
      setUserReactions(merged);

      // Clear pending reactions that are now in backend
      backendReactions.forEach(emoji => {
        pendingReactionsRef.current.delete(emoji);
      });
    }
  }, [existingReactions, currentPlayerId]);

  const handleReact = async (emoji) => {
    // Optimistic UI update
    const newReactions = [...userReactions];
    const index = newReactions.indexOf(emoji);

    if (index > -1) {
      newReactions.splice(index, 1);
      pendingReactionsRef.current.delete(emoji);
    } else {
      newReactions.push(emoji);
      pendingReactionsRef.current.add(emoji);
    }

    setUserReactions(newReactions);

    // Submit to backend
    try {
      await onReact(actionId, emoji);
      // Clear from pending after successful submission
      setTimeout(() => {
        pendingReactionsRef.current.delete(emoji);
      }, 3000); // Give backend 3 seconds to process
    } catch (error) {
      console.error('Failed to submit reaction:', error);
      // Revert on error
      pendingReactionsRef.current.delete(emoji);
      setUserReactions(userReactions);
    }
  };

  // Count total reactions by emoji, including current user's optimistic updates
  const reactionCounts = {};

  // First, count all reactions from backend
  Object.entries(existingReactions).forEach(([playerId, playerReactions]) => {
    // Skip current player's backend reactions - we'll use their optimistic state instead
    if (playerId === currentPlayerId) return;

    playerReactions.forEach(emoji => {
      reactionCounts[emoji] = (reactionCounts[emoji] || 0) + 1;
    });
  });

  // Add current user's reactions from optimistic state
  userReactions.forEach(emoji => {
    reactionCounts[emoji] = (reactionCounts[emoji] || 0) + 1;
  });

  // 📱 Compact chips (Nina 08-02: the 40px tiles read bulky, not modern) —
  // Discord-density pills. The 07-19 40px tap floor is CONSCIOUSLY relaxed
  // here: toggling a reaction is low-stakes, density wins.
  // 🫥 CONTAINERS GONE (Nina 08-03: "they just look so bad") — bare emoji +
  // count. The pill's two jobs move elsewhere: tap target = transparent
  // padding (unchanged hit box), your-reaction = accent count + emoji glow.
  const badgePadding = isMobile ? '3px 5px' : '4px 6px';
  const badgeFontSize = isMobile ? '14px' : '15px';
  const countFontSize = isMobile ? '10px' : '12px';
  const badgeGap = isMobile ? '2px' : '4px';
  // Highest counts first; past the cap they fold behind a +N chip.
  // chipCap (non-comp/house, Nina 08-30) caps on every form factor; legacy
  // behavior (mobile-only, 6) holds everywhere else.
  const sortedCounts = Object.entries(reactionCounts).sort((a, b) => b[1] - a[1]);
  const CHIP_CAP = chipCap != null ? chipCap : 6;
  const capActive = chipCap != null || isMobile;
  const capped = capActive && !showAllReactions && sortedCounts.length > CHIP_CAP + 1;
  const visibleCounts = capped ? sortedCounts.slice(0, CHIP_CAP) : sortedCounts;
  const overflowCount = sortedCounts.length - visibleCounts.length;
  const pickerEmojiSize = isMobile ? '26px' : '20px';
  const pickerPadding = isMobile ? '10px' : '8px';
  const pickerGap = isMobile ? '4px' : '6px';
  const pickerWidth = isMobile ? 'min(320px, 90vw)' : 'min(280px, 80vw)';
  // 🏡 readOnly (the place page, a guest): nothing to show = nothing at all
  if (readOnly && sortedCounts.length === 0) return null;
  const Chip = readOnly ? 'span' : 'button';
  const shield = propUi ? { 'data-prop-ui': '' } : {};

  return (
    <div style={{ marginTop: '0px', position: 'relative', zIndex: 10 }}>
      {/* Show existing reactions with counts */}
      <div style={{
        display: 'flex',
        gap: badgeGap,
        flexWrap: 'wrap',
        alignItems: 'center',
        ...(isMobile ? { width: '100%' } : {}),
      }}>
        {visibleCounts.map(([emoji, count]) => {
          const mine = userReactions.includes(emoji);
          return (
          <Chip
            key={emoji}
            {...shield}
            onClick={readOnly ? undefined : () => handleReact(emoji)}
            title={readOnly ? undefined : (mine ? 'Remove your reaction' : 'React')}
            style={{
              padding: badgePadding,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: readOnly ? 'default' : 'pointer',
              fontSize: badgeFontSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              transition: 'all 0.2s',
              minHeight: isMobile ? '26px' : undefined,
              ...(isMobile ? { flex: '0 0 auto', lineHeight: 1 } : {}),
            }}
          >
            <span style={mine ? { filter: `drop-shadow(0 0 4px ${theme.accent})` } : undefined}>{emoji}</span>
            <span style={{
              fontSize: countFontSize,
              color: mine ? theme.accent : theme.textMuted,
              fontWeight: 'bold',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {count}
            </span>
          </Chip>
          );
        })}
        {/* +N overflow — bot swarms fold behind one tap; "less" folds back */}
        {capActive && (capped || (showAllReactions && sortedCounts.length > CHIP_CAP + 1)) && (
          <button
            {...shield}
            onClick={() => setShowAllReactions(v => !v)}
            aria-label={capped ? `Show ${overflowCount} more reactions` : 'Show fewer reactions'}
            style={{
              padding: badgePadding,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              fontSize: countFontSize,
              fontWeight: 700,
              color: theme.textMuted,
              textDecoration: 'underline',
              minHeight: '26px',
              lineHeight: 1,
              display: 'flex', alignItems: 'center',
            }}
          >
            {capped ? `+${overflowCount}` : 'less'}
          </button>
        )}

        {/* Add reaction button */}
        {!readOnly && (
        <button
          ref={toggleRef}
          className={addClassName}
          {...shield}
          onClick={() => setShowPicker(!showPicker)}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
          style={{
            padding: badgePadding,
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            fontSize: badgeFontSize,
            color: theme.textMuted,
            opacity: 0.6,
            transition: 'opacity 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '3px',
            minHeight: isMobile ? '26px' : undefined,
            ...(isMobile ? { lineHeight: 1 } : {}),
          }}
        >
          <span>{showPicker ? '×' : '😊'}</span>
          {!showPicker && !hideAddLabel && Object.keys(reactionCounts).length === 0 && !isMobile && (
            <span style={{ fontSize: '11px', color: theme.textOnPanel || theme.textMuted || '#757575', fontWeight: 600 }}>Add reaction</span>
          )}
        </button>
        )}
      </div>

      {/* Reaction picker */}
      {showPicker && (
        <div ref={pickerRef} className={pickerClassName} style={{
          position: 'absolute',
          bottom: '100%',
          left: alignRight ? undefined : 0,
          right: alignRight ? 0 : undefined,
          marginBottom: '4px',
          backgroundColor: theme.bgLight,
          border: `1px solid ${theme.borderLight}`,
          borderRadius: isMobile ? '12px' : '8px',
          padding: pickerPadding,
          display: 'flex',
          flexWrap: 'wrap',
          width: pickerWidth,
          gap: pickerGap,
          boxShadow: '0 -2px 12px rgba(0,0,0,0.25)',
          zIndex: 1000
        }}>
          {reactions.map(emoji => (
            <button
              key={emoji}
              onClick={() => {
                handleReact(emoji);
                setShowPicker(false);
              }}
              style={{
                padding: isMobile ? '8px' : '6px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: pickerEmojiSize,
                borderRadius: '6px',
                transition: 'background-color 0.2s',
                minWidth: isMobile ? '40px' : undefined,
                minHeight: isMobile ? '40px' : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = `${theme.accent}20`;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageReactions;
